import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import OutletGoogleMap from "../../OutletManagement/_components/OutletGoogleMap";
import {
  IconSearch,
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconCalendarEvent,
  IconUsers,
  IconDatabase,
  IconUserCheck,
  IconReport,
  IconVolume,
  IconHierarchy,
  IconBriefcase,
  IconShield,
  IconLock,
  IconLockOpen,
  IconLoader2,
  IconAlertCircle,
  IconSettings,
  IconCheck,
  IconUser,
  IconTrash,
  IconEdit,
  IconCopy
} from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import CommonModal from "../../../components/common/CommonModal";
import NormalSearch from "../../../components/common/NormalSearch";
import { toast } from "react-hot-toast";
import SelectInput from "../../../components/Select";
import { GULF_COUNTRIES, getCountryAddressConfig } from "../../OrderManagement/_components/GulfMapPicker";
import {
  fetchHrCompany,
  fetchHrBranches,
  createHrBranch,
  updateHrBranch,
  deleteHrBranch,
  fetchHrCountries,
  fetchHrCities,
  fetchHrDistricts,
  HrBranchPayload,
  HrBranch,
  updateHrCompany,
  fetchHrRoles,
  fetchHrBranchConfig,
  syncHrBranchConfig,
  fetchHrGroupedPermissions,
  fetchHrRolePermissions,
  updateHrRolePermissions,
  createHrRole,
  updateHrRole,
  deleteHrRole
} from "../../../apis/hrApi";

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

const isUUID = (val?: string) =>
  !!val &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);




const getCompanyInitials = (companyName?: string): string => {
  if (!companyName) return "GT";
  return companyName
    .split(" ")
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("")
    .substring(0, 2);
};

const transformBranch = (branch: any) => {
  const locationParts = [branch.cityName, branch.countryName, branch.fullAddress]
    .filter(Boolean);

  return {
    ...branch,
    id: branch.id,
    name: branch.branchName || branch.branch_name || branch.name || "Untitled Branch",
    location: locationParts.join(", ") || "Unknown location",
    head: branch.managerName || branch.branchManagerName || branch.head || "Not assigned",
    employees: branch.employees?.length ?? branch.employeesCount ?? 0,
    status: branch.status || "Active",
    originalBranch: branch,
  };
};

interface CompanyInfo {
  id?: string;
  companyName?: string;
  email?: string;
  phoneNumber?: string;
  industry?: string;
  website?: string;
  foundedYear?: string;
  branches?: any[];
}

interface BranchFormData extends Partial<HrBranchPayload> {
  status?: string;
  latitude?: string;
  longitude?: string;
}

const cleanRoleCodeForUI = (code: string) => {
  if (!code) return "";
  const parts = code.split("-");
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1];
    if (lastPart.length === 8 && /^[A-F0-9]+$/i.test(lastPart)) {
      return parts.slice(0, -1).join("-");
    }
  }
  return code;
};

export default function Branches() {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showBranchSearch, setShowBranchSearch] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  const [page] = useState(1);
  const [limit] = useState(50);

  const [showBranchModal, setShowBranchModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState<HrBranch | null>(null);

  // Roles & Permissions states
  const [selectedBranchForRoles, setSelectedBranchForRoles] = useState<any | null>(null);
  const [branchRoles, setBranchRoles] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any | null>(null);
  const [branchConfig, setBranchConfig] = useState<any[]>([]);
  const [groupedPermissions, setGroupedPermissions] = useState<Record<string, any[]>>({});
  const [rolePermissions, setRolePermissions] = useState<any[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);
  const [isSyncingModules, setIsSyncingModules] = useState(false);

  // Role CRUD Modals
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [roleModalMode, setRoleModalMode] = useState<'create' | 'edit'>('create');
  const [roleForm, setRoleForm] = useState({
    roleName: '',
    roleCodeSuffix: '',
    description: '',
    status: 'ACTIVE'
  });
  const [isSubmittingRole, setIsSubmittingRole] = useState(false);

  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});

  const toggleExpandModule = (moduleName: string) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleName]: !prev[moduleName]
    }));
  };

  const handleSelectAllModule = (moduleName: string, isSelectAllChecked: boolean) => {
    const permissionsInModule = groupedPermissions[moduleName] || [];
    if (isSelectAllChecked) {
      // Uncheck all in this module
      const permIdsToRemove = new Set(permissionsInModule.map(p => p.id));
      setRolePermissions((prev) => prev.filter((rp) => !permIdsToRemove.has(rp.permissionId)));
    } else {
      // Check all in this module
      setRolePermissions((prev) => {
        const existingMap = new Map(prev.map(rp => [rp.permissionId, rp]));
        permissionsInModule.forEach((p) => {
          if (!existingMap.has(p.id)) {
            existingMap.set(p.id, {
              id: "",
              roleId: selectedRole?.id || "",
              permissionId: p.id,
              dataScope: "BRANCH"
            });
          }
        });
        return Array.from(existingMap.values());
      });
    }
  };

  // Fetch roles, config, permissions when branch changes
  useEffect(() => {
    if (selectedBranchForRoles && companyInfo) {
      const cid = companyInfo.id || (companyInfo as any)._id || "";
      const bid = selectedBranchForRoles.id;
      setIsLoadingRoles(true);
      setSelectedRole(null);

      // Fetch grouped permission keys once
      if (Object.keys(groupedPermissions).length === 0) {
        fetchHrGroupedPermissions()
          .then((res) => {
            if (res.success && res.data) {
              setGroupedPermissions(res.data);
            }
          })
          .catch((err) => console.error("Failed to fetch permissions layout:", err));
      }

      Promise.all([
        fetchHrRoles(cid, bid),
        fetchHrBranchConfig(cid, bid)
      ])
        .then(([rolesRes, configRes]) => {
          if (rolesRes.success && Array.isArray(rolesRes.data)) {
            setBranchRoles(rolesRes.data);
            if (rolesRes.data.length > 0) {
              setSelectedRole(rolesRes.data[0]);
            }
          }
          if (configRes.success && Array.isArray(configRes.data)) {
            setBranchConfig(configRes.data);
          }
        })
        .catch((err) => {
          toast.error(err?.message || "Failed to load roles / module config");
        })
        .finally(() => {
          setIsLoadingRoles(false);
        });
    }
  }, [selectedBranchForRoles, companyInfo]);

  // Fetch role permissions when role changes
  useEffect(() => {
    if (selectedRole) {
      setIsLoadingPermissions(true);
      fetchHrRolePermissions(selectedRole.id)
        .then((res) => {
          if (res.success && res.data) {
            const permsArray = Array.isArray(res.data)
              ? res.data
              : ((res.data as any).permissions || []);
            setRolePermissions(permsArray);

            // Auto-expand modules that have active permissions
            const activeModules: Record<string, boolean> = {};
            permsArray.forEach((rp: any) => {
              Object.keys(groupedPermissions).forEach((modName) => {
                const hasPerm = groupedPermissions[modName].some(p => p.id === rp.permissionId);
                if (hasPerm) {
                  activeModules[modName] = true;
                }
              });
            });
            setExpandedModules(activeModules);
          }
        })
        .catch((err) => {
          toast.error(err?.message || "Failed to load permissions for role");
        })
        .finally(() => {
          setIsLoadingPermissions(false);
        });
    } else {
      setRolePermissions([]);
    }
  }, [selectedRole]);

  // Toggle Module in Branch Config
  const handleToggleModule = (moduleName: string, currentEnabled: boolean) => {
    if (!companyInfo || !selectedBranchForRoles) return;
    const cid = companyInfo.id || (companyInfo as any)._id || "";
    const bid = selectedBranchForRoles.id;

    const newEnabledModules = branchConfig
      .map((c) => (c.module === moduleName ? { ...c, enabled: !currentEnabled } : c))
      .filter((c) => c.enabled)
      .map((c) => c.module);

    setIsSyncingModules(true);
    syncHrBranchConfig(cid, bid, newEnabledModules)
      .then(() => {
        toast.success(`Module "${moduleName}" ${!currentEnabled ? "enabled" : "disabled"} successfully`);
        return Promise.all([
          fetchHrBranchConfig(cid, bid),
          fetchHrRoles(cid, bid)
        ]);
      })
      .then(([configRes, rolesRes]) => {
        if (configRes.success && Array.isArray(configRes.data)) {
          setBranchConfig(configRes.data);
        }
        if (rolesRes.success && Array.isArray(rolesRes.data)) {
          setBranchRoles(rolesRes.data);
          if (selectedRole) {
            const updated = rolesRes.data.find((r) => r.id === selectedRole.id);
            if (updated) setSelectedRole(updated);
          }
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to update module config");
      })
      .finally(() => {
        setIsSyncingModules(false);
      });
  };

  // Toggle Role Permission Checkbox
  const handleTogglePermission = (permissionId: string, currentChecked: boolean) => {
    if (currentChecked) {
      setRolePermissions((prev) => prev.filter((p) => p.permissionId !== permissionId));
    } else {
      const newMapping = {
        id: "",
        roleId: selectedRole?.id || "",
        permissionId,
        dataScope: "BRANCH"
      };
      setRolePermissions((prev) => [...prev, newMapping]);
    }
  };

  // Change Permission Data Scope
  const handleChangeScope = (permissionId: string, newScope: string) => {
    setRolePermissions((prev) =>
      prev.map((rp) => (rp.permissionId === permissionId ? { ...rp, dataScope: newScope } : rp))
    );
  };

  // Save Role Permissions
  const handleSavePermissions = () => {
    if (!selectedRole) return;
    setIsSavingPermissions(true);
    const payload = rolePermissions.map((rp) => ({
      permissionId: rp.permissionId,
      dataScope: rp.dataScope || "BRANCH"
    }));

    updateHrRolePermissions(selectedRole.id, payload)
      .then(() => {
        toast.success("Role permissions updated successfully");
        if (companyInfo && selectedBranchForRoles) {
          const cid = companyInfo.id || (companyInfo as any)._id || "";
          const bid = selectedBranchForRoles.id;
          fetchHrRoles(cid, bid).then((rolesRes) => {
            if (rolesRes.success && Array.isArray(rolesRes.data)) {
              setBranchRoles(rolesRes.data);
              const updated = rolesRes.data.find((r) => r.id === selectedRole.id);
              if (updated) setSelectedRole(updated);
            }
          });
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to save permissions");
      })
      .finally(() => {
        setIsSavingPermissions(false);
      });
  };

  // Create or Update Role Submit
  const handleRoleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyInfo || !selectedBranchForRoles) return;
    if (!roleForm.roleName.trim()) {
      toast.error("Role name is required");
      return;
    }

    const cid = companyInfo.id || (companyInfo as any)._id || "";
    const bid = selectedBranchForRoles.id;
    setIsSubmittingRole(true);

    if (roleModalMode === "create") {
      const branchSuffix = bid.substring(0, 8).toUpperCase();
      const codeSuffix = roleForm.roleName.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "");
      if (!codeSuffix) {
        toast.error("Role name is required to generate a role code");
        setIsSubmittingRole(false);
        return;
      }
      const roleCode = `BRANCH-${codeSuffix}-${branchSuffix}`;

      createHrRole({
        companyId: cid,
        roleName: roleForm.roleName.trim(),
        roleCode,
        description: roleForm.description.trim(),
        status: roleForm.status
      })
        .then(() => {
          toast.success("Branch role created successfully");
          setIsRoleModalOpen(false);
          return fetchHrRoles(cid, bid);
        })
        .then((rolesRes) => {
          if (rolesRes && rolesRes.success && Array.isArray(rolesRes.data)) {
            setBranchRoles(rolesRes.data);
            const created = rolesRes.data.find((r) => r.roleName === roleForm.roleName.trim());
            if (created) setSelectedRole(created);
          }
        })
        .catch((err) => {
          toast.error(err?.message || "Failed to create role");
        })
        .finally(() => {
          setIsSubmittingRole(false);
        });
    } else {
      if (!selectedRole) return;
      updateHrRole(selectedRole.id, {
        roleName: roleForm.roleName.trim(),
        description: roleForm.description.trim(),
        status: roleForm.status
      })
        .then(() => {
          toast.success("Role updated successfully");
          setIsRoleModalOpen(false);
          return fetchHrRoles(cid, bid);
        })
        .then((rolesRes) => {
          if (rolesRes && rolesRes.success && Array.isArray(rolesRes.data)) {
            setBranchRoles(rolesRes.data);
            const updated = rolesRes.data.find((r) => r.id === selectedRole.id);
            if (updated) setSelectedRole(updated);
          }
        })
        .catch((err) => {
          toast.error(err?.message || "Failed to update role");
        })
        .finally(() => {
          setIsSubmittingRole(false);
        });
    }
  };

  const openCreateRoleModal = () => {
    setRoleModalMode("create");
    setRoleForm({
      roleName: "",
      roleCodeSuffix: "",
      description: "",
      status: "ACTIVE"
    });
    setIsRoleModalOpen(true);
  };

  const openEditRoleModal = (role: any) => {
    setRoleModalMode("edit");
    setRoleForm({
      roleName: role.roleName,
      roleCodeSuffix: "",
      description: role.description || "",
      status: role.status
    });
    setIsRoleModalOpen(true);
  };

  const handleDeleteRole = (role: any) => {
    if (!window.confirm(`Are you sure you want to delete the role "${role.roleName}"?`)) return;
    deleteHrRole(role.id)
      .then(() => {
        toast.success("Role deleted successfully");
        if (companyInfo && selectedBranchForRoles) {
          const cid = companyInfo.id || (companyInfo as any)._id || "";
          const bid = selectedBranchForRoles.id;
          return fetchHrRoles(cid, bid);
        }
      })
      .then((rolesRes) => {
        if (rolesRes && rolesRes.success && Array.isArray(rolesRes.data)) {
          setBranchRoles(rolesRes.data);
          if (rolesRes.data.length > 0) {
            setSelectedRole(rolesRes.data[0]);
          } else {
            setSelectedRole(null);
          }
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to delete role");
      });
  };

  // Map state
  const [pinPosition, setPinPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [snappedPosition, setSnappedPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [shortAddressLoading, setShortAddressLoading] = useState(false);
  const [shortAddressError, setShortAddressError] = useState("");
  const [shortAddressWarning, setShortAddressWarning] = useState("");
  const [pendingAddressInfo, setPendingAddressInfo] = useState<{
    countryName?: string;
    cityName?: string;
    districtName?: string;
  } | null>(null);
  const isPinnedRef = useRef(false);
  const API_BASE = "https://track-map.gwarm.net/api/areas";

  const [branchFormData, setBranchFormData] = useState<BranchFormData>({
    branchCode: "",
    branchName: "",
    branchType: "BRANCH",
    email: "",
    phoneNumber: "",
    countryId: "",
    cityId: "",
    districtId: "",
    fullAddress: "",
    shortAddress: "",
    zipCode: "",
    status: "ACTIVE",
  });
  const [countries, setCountries] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [formIsLoading, setFormIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyFormData, setCompanyFormData] = useState<Partial<CompanyInfo>>({});
  const [companyFormIsLoading, setCompanyFormIsLoading] = useState(false);
  const [companyFormError, setCompanyFormError] = useState<string | null>(null);

  const countryCode = useMemo(() => {
    if (!branchFormData.countryId) return "SA";
    const country = countries.find(c => String(c.id) === String(branchFormData.countryId));
    if (country) {
      if (country.iso2) return country.iso2;
      if (country.countryCode) return country.countryCode;
      const codeFromMap = GULF_COUNTRIES.find(c => c.name.toLowerCase() === country.name.toLowerCase())?.code;
      if (codeFromMap) return codeFromMap;
    }
    return "SA";
  }, [branchFormData.countryId, countries]);

  const addressConfig = useMemo(() => {
    return getCountryAddressConfig(countryCode);
  }, [countryCode]);

  const isSaudiArabia = useMemo(() => {
    return countryCode === "SA";
  }, [countryCode]);

  const handleOpenCompanyModal = () => {
    setCompanyFormData({
      companyName: companyInfo?.companyName || "",
      email: companyInfo?.email || "",
      phoneNumber: companyInfo?.phoneNumber || "",
      industry: companyInfo?.industry || "Technology & HR",
      website: companyInfo?.website || "https://www.gwarm.net/",
      foundedYear: companyInfo?.foundedYear || "2018",
    });
    setShowCompanyModal(true);
    setCompanyFormError(null);
  };

  const handleCloseCompanyModal = () => {
    setShowCompanyModal(false);
  };

  const handleCompanyFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCompanyFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompanyFormSubmit = async () => {
    const cid = companyInfo?.id || (companyInfo as any)?._id || "default";
    setCompanyFormIsLoading(true);
    setCompanyFormError(null);
    try {
      await updateHrCompany(cid, companyFormData);
      setCompanyInfo({ ...companyInfo, ...companyFormData });
      handleCloseCompanyModal();
    } catch (err) {
      setCompanyFormError(err instanceof Error ? err.message : "Failed to update company details");
    } finally {
      setCompanyFormIsLoading(false);
    }
  };

  useEffect(() => {
    if (!pendingAddressInfo) return;

    if (pendingAddressInfo.countryName && countries.length > 0) {
      const countryObj = countries.find(
        (c: any) => c.name.trim().toLowerCase() === pendingAddressInfo.countryName?.trim().toLowerCase() ||
          (pendingAddressInfo.countryName === "Saudi Arabia" && c.iso2 === "SA")
      );

      setPendingAddressInfo((prev: any) => ({ ...prev, countryName: undefined }));

      if (countryObj) {
        if (branchFormData.countryId !== countryObj.id) {
          setBranchFormData((prev) => ({ ...prev, countryId: countryObj.id }));
          fetchHrCities(countryObj.id).then((res: any) => {
            if (res.success && res.data?.cities) setCities(res.data.cities);
          });
          return;
        }
      }
    }

    if (pendingAddressInfo.cityName && cities.length > 0) {
      const cityObj = cities.find(
        (c: any) => c.name.trim().toLowerCase() === pendingAddressInfo.cityName?.trim().toLowerCase()
      );

      setPendingAddressInfo((prev: any) => ({ ...prev, cityName: undefined }));

      if (cityObj) {
        if (branchFormData.cityId !== cityObj.id) {
          setBranchFormData((prev) => ({ ...prev, cityId: cityObj.id }));
          fetchHrDistricts(cityObj.id).then((res: any) => {
            if (res.success && res.data?.districts) setDistricts(res.data.districts);
          });
          return;
        }
      } else {
        // City not found in list (custom value)
        setBranchFormData((prev) => ({
          ...prev,
          cityId: pendingAddressInfo.cityName || "",
          districtId: pendingAddressInfo.districtName || ""
        }));
        setPendingAddressInfo(null);
        return;
      }
    }

    if (pendingAddressInfo.districtName) {
      const districtObj = districts.find(
        (d: any) => d.name.trim().toLowerCase() === pendingAddressInfo.districtName?.trim().toLowerCase()
      );

      if (districtObj) {
        setBranchFormData((prev) => ({ ...prev, districtId: districtObj.id }));
      } else {
        setBranchFormData((prev) => ({ ...prev, districtId: pendingAddressInfo.districtName || "" }));
      }
      setPendingAddressInfo(null);
    } else if (pendingAddressInfo && !pendingAddressInfo.countryName && !pendingAddressInfo.cityName) {
      setPendingAddressInfo(null);
    }
  }, [countries, cities, districts, pendingAddressInfo, branchFormData.countryId, branchFormData.cityId]);

  const handleMapPick = useCallback((locData: any) => {
    const { lat, lng, address, shortAddress, cityName, districtName, source } = locData;
    setPinPosition({ lat, lng });
    if (source === "redis_geo" || source === "database_zone") {
      setSnappedPosition({ lat, lng });
    } else {
      setSnappedPosition(null);
    }

    setBranchFormData((prev) => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString(),
      fullAddress: address,
      shortAddress: shortAddress || prev.shortAddress,
    }));

    setPendingAddressInfo({
      countryName: locData.countryName || "Saudi Arabia",
      cityName: cityName,
      districtName: districtName,
    });
    isPinnedRef.current = true;
  }, []);

  const handleShortAddressChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.toUpperCase();
    setBranchFormData((prev) => ({ ...prev, shortAddress: val }));
    setShortAddressWarning("");
    setShortAddressError("");

    if (val.length === 8) {
      setShortAddressLoading(true);
      try {
        const res = await fetch(`${API_BASE}/adrress-get-by-short-address?shortAddress=${val}`);
        const apiData = await res.json();
        if (apiData?.success && apiData?.data) {
          const addr = apiData.data;
          setShortAddressError("");
          setBranchFormData((prev) => ({
            ...prev,
            fullAddress: `${addr.buildingNumber || ""} ${addr.street || ""} ${addr.district || ""} ${addr.city || ""}`.trim(),
            latitude: addr.latitude?.toString(),
            longitude: addr.longitude?.toString(),
          }));
          if (addr.latitude && addr.longitude) {
            const pos = { lat: parseFloat(addr.latitude), lng: parseFloat(addr.longitude) };
            setPinPosition(pos);
            setSnappedPosition(pos);
          }
          setPendingAddressInfo({
            countryName: "Saudi Arabia",
            cityName: addr.city,
            districtName: addr.district,
          });
        } else {
          setShortAddressError("Short code not found.");
        }
      } catch (err) {
        setShortAddressError("Could not verify short address.");
      } finally {
        setShortAddressLoading(false);
      }
    } else if (val.length === 0) {
      setPinPosition(null);
      setSnappedPosition(null);
    }
  }, []);

  const handleToggleMap = useCallback(() => {
    setShowMap(prev => {
      if (!prev) isPinnedRef.current = false;
      return !prev;
    });
  }, []);

  useEffect(() => {
    const loadHrData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [companyResponse, branchResponse] = await Promise.all([
          fetchHrCompany(),
          fetchHrBranches(page, limit),
        ]);

        if (companyResponse.success && companyResponse.data) {
          setCompanyInfo(companyResponse.data);
        }

        if (branchResponse.success && Array.isArray(branchResponse.data?.branches)) {
          const companyBranches = branchResponse.data.branches.map(transformBranch);
          setBranches(companyBranches);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load HR data");
      } finally {
        setIsLoading(false);
      }
    };

    loadHrData();
  }, [page, limit]);

  const loadLocationData = async (currentCountryId?: string, currentCityId?: string) => {
    try {
      const targetCountryId = currentCountryId || branchFormData.countryId;
      const targetCityId = currentCityId || branchFormData.cityId;

      const [countriesRes, citiesRes, districtsRes] = await Promise.all([
        fetchHrCountries(),
        targetCountryId && isUUID(targetCountryId) ? fetchHrCities(targetCountryId) : Promise.resolve({ success: true, data: { cities: [] } }),
        targetCityId && isUUID(targetCityId) ? fetchHrDistricts(targetCityId) : Promise.resolve({ success: true, data: { districts: [] } }),
      ]);
      if (countriesRes.success && countriesRes.data?.countries) {
        setCountries(countriesRes.data.countries);
      }
      if ((citiesRes as any).success && (citiesRes as any).data?.cities) {
        setCities((citiesRes as any).data.cities);
      }
      if ((districtsRes as any).success && (districtsRes as any).data?.districts) {
        setDistricts((districtsRes as any).data.districts);
      }
    } catch (err) {
      console.error("Failed to load location data:", err);
    }
  };

  const handleOpenBranchModal = async (branch?: HrBranch) => {
    if (branch) {
      setEditingBranch(branch);
      setBranchFormData({
        branchCode: branch.branchCode,
        branchName: branch.branchName,
        branchType: branch.branchType,
        email: branch.email || "",
        phoneNumber: branch.phoneNumber || "",
        countryId: branch.countryId,
        cityId: branch.cityId,
        districtId: branch.districtId,
        fullAddress: branch.fullAddress,
        shortAddress: branch.shortAddress || "",
        zipCode: branch.zipCode || "",
        latitude: branch.latitude ? String(branch.latitude) : "",
        longitude: branch.longitude ? String(branch.longitude) : "",
        status: branch.status,
      });
      setPinPosition(branch.latitude && branch.longitude ? { lat: Number(branch.latitude), lng: Number(branch.longitude) } : null);
    } else {
      setEditingBranch(null);
      setBranchFormData({
        branchCode: "",
        branchName: "",
        branchType: "BRANCH",
        email: companyInfo?.email || "",
        phoneNumber: companyInfo?.phoneNumber || "",
        countryId: "",
        cityId: "",
        districtId: "",
        fullAddress: "",
        shortAddress: "",
        zipCode: "",
        latitude: "",
        longitude: "",
        status: "ACTIVE",
      });
      setPinPosition(null);
    }
    setSnappedPosition(null);
    setShowMap(false);
    setShortAddressError("");
    setShortAddressWarning("");
    setPendingAddressInfo(null);
    isPinnedRef.current = !!(branch?.latitude && branch?.longitude);

    await loadLocationData(branch?.countryId, branch?.cityId);
    setShowBranchModal(true);
    setFormError(null);
  };

  const handleCloseBranchModal = () => {
    setShowBranchModal(false);
    setEditingBranch(null);
    setBranchFormData({
      branchCode: "",
      branchName: "",
      branchType: "BRANCH",
      email: "",
      phoneNumber: "",
      countryId: "",
      cityId: "",
      districtId: "",
      fullAddress: "",
      shortAddress: "",
      zipCode: "",
      status: "ACTIVE",
    });
  };

  const handleBranchFormChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBranchFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Load cities when country changes
    if (name === "countryId") {
      setCities([]);
      setDistricts([]);
      setBranchFormData((prev) => ({ ...prev, cityId: "", districtId: "" }));
      if (value && isUUID(value)) {
        try {
          const citiesRes = await fetchHrCities(value);
          if ((citiesRes as any).success && (citiesRes as any).data?.cities) {
            setCities((citiesRes as any).data.cities);
          }
        } catch (err) {
          console.error("Failed to load cities:", err);
        }
      }
    }

    // Load districts when city changes
    if (name === "cityId") {
      setDistricts([]);
      setBranchFormData((prev) => ({ ...prev, districtId: "" }));
      if (value && isUUID(value)) {
        try {
          const districtsRes = await fetchHrDistricts(value);
          if ((districtsRes as any).success && (districtsRes as any).data?.districts) {
            setDistricts((districtsRes as any).data.districts);
          }
        } catch (err) {
          console.error("Failed to load districts:", err);
        }
      }
    }
  };

  const handleBranchFormSubmit = async () => {
    if (!branchFormData.branchName || !branchFormData.countryId || !branchFormData.cityId || !branchFormData.districtId) {
      setFormError("Please fill in all required fields");
      return;
    }

    setFormIsLoading(true);
    setFormError(null);

    try {
      const payload: HrBranchPayload = {
        companyId: companyInfo?.id || (companyInfo as any)?._id || "",
        branchCode: branchFormData.branchCode || "BR-001",
        branchName: branchFormData.branchName || "",
        branchType: branchFormData.branchType || "BRANCH",
        email: branchFormData.email,
        phoneNumber: branchFormData.phoneNumber,
        countryId: branchFormData.countryId || "",
        cityId: branchFormData.cityId || "",
        districtId: branchFormData.districtId || "",
        fullAddress: branchFormData.fullAddress || "",
        shortAddress: branchFormData.shortAddress || null,
        zipCode: branchFormData.zipCode || null,
        latitude: branchFormData.latitude ? Number(branchFormData.latitude) : null,
        longitude: branchFormData.longitude ? Number(branchFormData.longitude) : null,
        status: branchFormData.status || "ACTIVE",
      };

      if (editingBranch) {
        await updateHrBranch(editingBranch.id, payload);
      } else {
        await createHrBranch(payload);
      }

      // Reload branches
      const branchResponse = await fetchHrBranches(page, limit);
      if (branchResponse.success && Array.isArray(branchResponse.data?.branches)) {
        const companyBranches = branchResponse.data.branches.map(transformBranch);
        setBranches(companyBranches);
      }

      handleCloseBranchModal();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to save branch");
    } finally {
      setFormIsLoading(false);
    }
  };

  const handleDeleteBranch = async (branchId: string) => {
    if (!window.confirm("Are you sure you want to delete this branch?")) return;

    try {
      await deleteHrBranch(branchId);
      // Reload branches
      const branchResponse = await fetchHrBranches(page, limit);
      if (branchResponse.success && Array.isArray(branchResponse.data?.branches)) {
        const companyBranches = branchResponse.data.branches.map(transformBranch);
        setBranches(companyBranches);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete branch");
    }
  };

  const branchColumnsWithHandlers = [
    {
      label: (
        <label className="relative flex items-center justify-center cursor-pointer">
          <input
            type="checkbox"
            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
          />
          <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            ✓
          </span>
        </label>
      ),
      render: () => (
        <label className="relative flex items-center justify-center cursor-pointer">
          <input
            type="checkbox"
            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
          />
          <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            ✓
          </span>
        </label>
      )
    },
    { label: "SR NO.", render: (_: any, i: number) => <span className="font-medium text-gray-600">{i + 1}</span> },
    { label: "BRANCH NAME", render: (s: any) => <span className="font-semibold text-[#212121]">{s.name}</span> },
    { label: "LOCATION", render: (s: any) => <span className="text-sm text-gray-700">{s.location}</span> },
    { label: "HEAD", render: (s: any) => <span className="text-sm text-gray-700">{s.head}</span> },
    { label: "EMPLOYEES", render: (s: any) => <span className="text-sm text-gray-700 font-medium">{s.employees}</span> },
    {
      label: "STATUS", render: (s: any) => {
        const active = typeof s.status === "string" && s.status.toLowerCase() === "active";
        return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${active ? "text-green-600 bg-green-50 border-green-200" : "text-red-600 bg-red-50 border-red-200"}`}>● {s.status}</span>;
      }
    },
    { label: "ACTIONS", render: (s: any) => <div className="flex justify-center gap-2"><button onClick={() => setSelectedBranchForRoles(s)} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-yellow-500 hover:bg-yellow-50 transition text-gray-500 hover:text-yellow-600" title="Roles & Permissions"><IconShield size={16} /></button><button onClick={() => handleOpenBranchModal(s)} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#FBD037] hover:bg-[#FBD037]/10 transition text-gray-500 hover:text-[#212121]"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg></button><button onClick={() => handleDeleteBranch(s.id)} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-red-400 hover:bg-red-50 transition text-gray-500 hover:text-red-500"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg></button></div> },
  ];

  const filteredBranches = useMemo(() => {
    if (!searchQuery.trim()) return branches;
    const query = searchQuery.toLowerCase();
    return branches.filter((branch) =>
      [branch.name, branch.location, branch.head, branch.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [branches, searchQuery]);

  if (selectedBranchForRoles) {
    return (
      <div className="w-full mx-auto animate-in fade-in duration-300">
        {/* Header Bar — matches site-wide pattern */}
        <div className="flex items-center">
          <div className="bg-[#F7F6F2] flex items-center justify-between px-6 py-3 w-full rounded-tl-3xl rounded-tr-[40px]">
            <div>

              <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
                Roles &amp; Permissions — {selectedBranchForRoles.name}
              </h1>
            </div>
            <button
              onClick={() => setSelectedBranchForRoles(null)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-white rounded-full text-xs font-semibold text-gray-700 transition shadow-sm"
            >
              <IconChevronLeft size={15} />
              Back to Branches
            </button>
          </div>
        </div>

        {/* Roles & Permissions Grid Layout */}
        <div className="bg-[#FCFDF8] rounded-b-[2rem] border border-t-0 border-gray-100 overflow-hidden shadow-sm">
          <div className="grid grid-cols-12 divide-x divide-[#000000]/10">
            {/* Left Column: Roles list */}
            <div className="col-span-12 lg:col-span-4 border-r border-[#000000]/10 overflow-hidden flex flex-col bg-[#F7F6F2] min-h-[600px]">
              <div className="flex justify-between items-center py-3 px-4 border-b border-[#000000]/10 sticky top-0 bg-[#F7F6F2] z-10">
                <h3 className="text-sm font-semibold text-[#212121] tracking-[-0.3px] flex items-center gap-2">
                  <IconShield size={16} className="text-[#FBD037]" />
                  Branch Roles
                </h3>
                <button
                  onClick={openCreateRoleModal}
                  className="flex items-center gap-1 p-2.5 text-sm rounded-full bg-[#FBD037] hover:bg-[#212121] hover:text-white text-[#212121] transition shadow-sm"
                  title="Add Role"
                >
                  <IconPlus stroke={1.5} size={22} />
                </button>
              </div>

              {isLoadingRoles ? (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                  <IconLoader2 className="animate-spin text-[#FBD037] mb-2" size={32} />
                  <span className="text-sm">Loading roles...</span>
                </div>
              ) : branchRoles.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                  <span className="text-sm">No roles found for this branch.</span>
                </div>
              ) : (
                <ul className="overflow-y-auto h-full divide-y divide-[#000000]/10 bg-[#F7F6F2]">
                  {branchRoles.map((role) => {
                    const isSelected = selectedRole?.id === role.id;
                    const isSystem = role.isSystemRole || role.roleCode === "ADMIN" || role.roleCode === "EMPLOYEE" || role.roleCode.startsWith("SYSTEM-");
                    return (
                      <li
                        key={role.id}
                        onClick={() => setSelectedRole(role)}
                        className={`flex items-center justify-between px-4 py-3 cursor-pointer ${isSelected ? "bg-[#FBD037]" : "hover:bg-black/5"
                          }`}
                      >
                        <div className="flex items-center gap-3 min-w-0 pr-3">
                          <div className="w-10 h-10 rounded-full bg-[#fff] text-[#212121] font-medium text-base flex items-center justify-center uppercase flex-shrink-0">
                            {role.roleName?.charAt(0)}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-base text-[#212121] font-medium truncate">
                                {role.roleName}
                              </span>
                              {isSystem && (
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider bg-gray-100 px-1.5 py-0.5 rounded">
                                  System
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="relative space-x-2 flex-shrink-0">
                          {!isSystem && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditRoleModal(role);
                                }}
                                className="p-2 border rounded-full bg-[#F7F6F2]"
                                title="Edit Role"
                              >
                                <IconEdit strokeWidth={1.5} size={20} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRole(role);
                                }}
                                className="p-2 border rounded-full bg-[#F7F6F2] hover:bg-red-50 hover:border-red-200"
                                title="Delete Role"
                              >
                                <IconTrash strokeWidth={1.5} size={20} className="text-red-500" />
                              </button>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Right Column: Permissions matrix */}
            <div className="col-span-12 lg:col-span-8 h-full flex flex-col bg-[#FCFDF8] min-h-[600px] p-6">
              {/* Super Admin Module Sync Controls */}
              <div className="mb-5 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col gap-3">
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <IconSettings size={13} />
                  Branch Modules Enabled by SuperAdmin
                </h4>
                <div className="flex flex-wrap gap-2">
                  {branchConfig.length === 0 ? (
                    <span className="text-xs text-gray-400">No configurations loaded</span>
                  ) : (
                    branchConfig.map((cfg) => (
                      <div
                        key={cfg.module}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${cfg.enabled
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-gray-50 text-gray-400 border-gray-100 opacity-60"
                          }`}
                      >
                        {cfg.enabled ? <IconLockOpen className="text-green-600" size={13} /> : <IconLock className="text-gray-400" size={13} />}
                        <span className="capitalize">{cfg.module}</span>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${cfg.enabled ? "bg-green-100 text-green-800" : "bg-gray-200/60 text-gray-500"
                          }`}>
                          {cfg.enabled ? "Active" : "Inactive"}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Role Header & Save Action */}
              <div className="flex justify-between items-center mb-5 pb-4 border-b border-[#000000]/10 flex-wrap gap-2">
                <div>
                  <h3 className="text-base font-semibold text-[#212121] tracking-[-0.3px]">
                    {selectedRole ? `Permissions for ${selectedRole.roleName}` : "Permissions"}
                  </h3>
                  <p className="text-[11px] text-gray-400 mt-0.5">Check operations to grant or deny them to this role.</p>
                </div>
                {selectedRole && (
                  <button
                    onClick={handleSavePermissions}
                    disabled={isSavingPermissions || isLoadingPermissions}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#FBD037] hover:bg-[#FACC15] disabled:bg-gray-100 text-[#212121] disabled:text-gray-400 rounded-full text-xs font-semibold transition shadow-sm animate-in fade-in duration-200"
                  >
                    {isSavingPermissions ? (
                      <IconLoader2 className="animate-spin" size={14} />
                    ) : (
                      <IconCheck size={14} />
                    )}
                    Save Permissions
                  </button>
                )}
              </div>

              {/* Permission list */}
              {isLoadingPermissions || !selectedRole ? (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                  {isLoadingPermissions ? (
                    <>
                      <IconLoader2 className="animate-spin text-[#FBD037] mb-2" size={32} />
                      <span className="text-sm">Loading role permissions...</span>
                    </>
                  ) : (
                    <span className="text-sm">Select a role on the left to configure permissions.</span>
                  )}
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto max-h-[500px] space-y-3 pr-1 scrollbar-clean">
                  {Object.keys(groupedPermissions)
                    .filter((moduleName) => {
                      if (!branchConfig || branchConfig.length === 0) return true;
                      const moduleConfig = branchConfig.find(
                        (c) => c.module.toLowerCase() === moduleName.toLowerCase()
                      );
                      return moduleConfig ? moduleConfig.enabled : false;
                    })
                    .map((moduleName) => {
                      const permissionsInModule = groupedPermissions[moduleName];
                      const activePermsCount = permissionsInModule.filter(p =>
                        rolePermissions.some(rp => rp.permissionId === p.id)
                      ).length;
                      const isAllSelected = permissionsInModule.length > 0 && activePermsCount === permissionsInModule.length;
                      const isExpanded = !!expandedModules[moduleName];

                      const getModuleIcon = (modName: string) => {
                        const size = 16;
                        switch (modName.toLowerCase()) {
                          case "attendance":
                            return <IconCalendarEvent size={size} className="text-[#3B82F6]" />;
                          case "employees":
                            return <IconUsers size={size} className="text-[#10B981]" />;
                          case "payroll":
                            return <IconDatabase size={size} className="text-[#F59E0B]" />;
                          case "hr":
                            return <IconUserCheck size={size} className="text-[#8B5CF6]" />;
                          case "reports":
                            return <IconReport size={size} className="text-[#EF4444]" />;
                          case "settings":
                            return <IconSettings size={size} className="text-[#6B7280]" />;
                          case "noticeboard":
                            return <IconVolume size={size} className="text-[#EC4899]" />;
                          case "departments":
                            return <IconHierarchy size={size} className="text-[#06B6D4]" />;
                          case "designations":
                            return <IconBriefcase size={size} className="text-[#14B8A6]" />;
                          default:
                            return <IconShield size={size} className="text-gray-500" />;
                        }
                      };

                      return (
                        <div
                          key={moduleName}
                          className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden transition-all duration-200"
                        >
                          {/* Accordion Header */}
                          <div
                            onClick={() => toggleExpandModule(moduleName)}
                            className="flex justify-between items-center px-4 py-3 bg-gray-50/50 hover:bg-gray-55 cursor-pointer select-none transition-colors border-b border-gray-100"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              {getModuleIcon(moduleName)}
                              <h4 className="font-semibold text-xs text-[#212121] uppercase tracking-wider capitalize truncate">
                                {moduleName}
                              </h4>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${activePermsCount > 0
                                  ? "bg-[#FBD037]/20 text-[#855B00]"
                                  : "bg-gray-100 text-gray-500"
                                }`}>
                                {activePermsCount} of {permissionsInModule.length} active
                              </span>
                            </div>

                            <div className="flex items-center gap-3.5" onClick={(e) => e.stopPropagation()}>
                              {/* Select All Toggle */}
                              <label className="flex items-center gap-1.5 cursor-pointer text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-white border border-gray-200 hover:border-gray-300 px-2.5 py-1 rounded-lg transition">
                                <input
                                  type="checkbox"
                                  checked={isAllSelected}
                                  onChange={() => handleSelectAllModule(moduleName, isAllSelected)}
                                  className="peer h-3.5 w-3.5 cursor-pointer rounded border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] transition-colors"
                                />
                                Select All
                              </label>

                              {/* Expand/Collapse Toggle */}
                              <button
                                onClick={() => toggleExpandModule(moduleName)}
                                className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200/50 transition-colors"
                              >
                                {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                              </button>
                            </div>
                          </div>

                          {/* Accordion Content */}
                          {isExpanded && (
                            <div className="p-4 bg-white grid grid-cols-1 md:grid-cols-3 gap-2.5 animate-in slide-in-from-top-2 duration-200">
                              {permissionsInModule.map((p) => {
                                const isChecked = rolePermissions.some((rp) => rp.permissionId === p.id);
                                return (
                                  <label
                                    key={p.id}
                                    className={`flex items-center gap-3 p-2.5 rounded-xl border text-xs font-medium cursor-pointer transition-all ${isChecked
                                        ? "bg-[#FBD037]/10 border-[#FBD037] text-[#212121]"
                                        : "border-gray-200 bg-[#F7F6F2] text-gray-600 hover:bg-[#F7F6F2]/80"
                                      }`}
                                  >
                                    <div className="relative flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleTogglePermission(p.id, isChecked)}
                                        className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] transition-all bg-white checked:bg-[#FBD037]"
                                      />
                                      <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                                        ✓
                                      </span>
                                    </div>
                                    <div className="flex flex-col flex-1 min-w-0">
                                      <span className="font-semibold capitalize text-[11px] truncate">
                                        {p.action}
                                      </span>
                                      <span className="text-[9px] text-gray-400 font-mono mt-0.5 truncate">
                                        {p.permissionKey}
                                      </span>
                                      {isChecked && (
                                        <div className="mt-1.5 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                          <span className="text-[8px] text-gray-400 font-semibold uppercase tracking-wider flex-shrink-0">Scope:</span>
                                          <select
                                            value={rolePermissions.find((rp) => rp.permissionId === p.id)?.dataScope || "BRANCH"}
                                            onChange={(e) => handleChangeScope(p.id, e.target.value)}
                                            className="text-[10px] bg-white border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#FBD037] font-semibold text-gray-700 w-full"
                                          >
                                            <option value="OWN">Own</option>
                                            <option value="TEAM">Team</option>
                                            <option value="DEPARTMENT">Department</option>
                                            <option value="BRANCH">Branch</option>
                                            <option value="COMPANY">Company</option>
                                            <option value="ALL">All</option>
                                          </select>
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create / Edit Role Modal */}
        <CommonModal
          open={isRoleModalOpen}
          title={roleModalMode === "create" ? "Create Custom Role" : "Edit Custom Role"}
          onClose={() => setIsRoleModalOpen(false)}
          actionButtonText={roleModalMode === "create" ? "Create Role" : "Save Changes"}
          actionButtonOnClick={handleRoleSubmit}
          isPending={isSubmittingRole}
        >
          <div className="p-6 bg-white space-y-4 min-w-[450px]">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={roleForm.roleName}
                onChange={(e) => setRoleForm({ ...roleForm, roleName: e.target.value })}
                className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037]"
                placeholder="e.g. Attendance Manager"
              />
            </div>

            {/* {roleModalMode === "create" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Role Code Suffix <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold font-mono text-gray-400">
                    BRANCH-
                  </span>
                  <input
                    type="text"
                    required
                    value={roleForm.roleCodeSuffix}
                    onChange={(e) => setRoleForm({ ...roleForm, roleCodeSuffix: e.target.value.toUpperCase() })}
                    className="flex-1 px-3.5 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037]"
                    placeholder="e.g. ATTENDANCE"
                  />
                  <span className="text-sm font-bold font-mono text-gray-400">
                    -{selectedBranchForRoles.id.substring(0, 8).toUpperCase()}
                  </span>
                </div>
                <p className="text-[11px] text-gray-400 mt-1 font-mono">
                  Full Code will be: BRANCH-{roleForm.roleCodeSuffix || "SUFFIX"}-{selectedBranchForRoles.id.substring(0, 8).toUpperCase()}
                </p>
              </div>
            )} */}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037]"
                placeholder="Brief description of the role's duties"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Status
              </label>
              <select
                value={roleForm.status}
                onChange={(e) => setRoleForm({ ...roleForm, status: e.target.value })}
                className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037]"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>

        </CommonModal>
      </div>
    );
  }

  return (
    <div className="p-6 pt-4 space-y-6 max-w-[95rem] mx-auto">
      {/* Company Details Card */}
      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-8 flex items-start gap-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-2 h-full bg-[#FBD037]"></div>

        <div className="w-32 h-32 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 p-4">
          {/* Fake Logo */}
          <div className="w-full h-full bg-[#FBD037] rounded-xl flex items-center justify-center">
            <span className="text-4xl font-black text-[#212121]">
              {getCompanyInitials(companyInfo?.companyName)}
            </span>
          </div>
        </div>

        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-3xl font-bold text-[#212121] tracking-tight mb-2">
                {companyInfo?.companyName || "Gwarm Technologies Private Limited"}
              </h2>
              <p className="text-gray-500 text-sm max-w-2xl">Modern human resources management system designed to streamline your workforce operations, payroll, and recruitment across multiple global branches.</p>
            </div>
            <button onClick={handleOpenCompanyModal} className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm font-semibold text-gray-700 transition border border-gray-200 shadow-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              Edit Details
            </button>
          </div>

          <div className="mt-8 grid grid-cols-4 gap-6">
            <div>
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Industry</div>
              <div className="font-semibold text-[#212121]">{companyInfo?.industry || "Technology & HR"}</div>
            </div>
            <div>
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Founded</div>
              <div className="font-semibold text-[#212121]">{companyInfo?.foundedYear || "2018"}</div>
            </div>
            <div>
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Website</div>
              <div className="font-semibold text-blue-600 hover:underline cursor-pointer">{companyInfo?.website || "https://www.gwarm.net/"}</div>
            </div>
            <div>
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Global Workforce</div>
              <div className="font-semibold text-[#212121]">{branches.reduce((acc, curr) => acc + Number(curr.employees || 0), 0)} Employees</div>
            </div>
          </div>
        </div>
      </div>

      {/* Branches Section */}
      <div className="w-full mx-auto mt-6">
        {/* Outer gradient card background — same as Order table */}
        <div className="relative p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-full rounded-r-3xl rounded-bl-3xl">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
          <div className="relative z-10 grid grid-cols-12 gap-4">
            {/* Table Card Column */}
            <div className={`transition-all duration-300 ${showBranchModal ? 'col-span-12 xl:col-span-7' : 'col-span-12'}`}>
              {/* Table Card */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-visible mb-6">
                {/* Toolbar */}
                <div className="px-4 py-1.5 flex items-center justify-end gap-2.5 border-b border-slate-50">
                  {/* Sliding Search */}
                  <div className="relative flex items-center">
                    <div
                      onClick={() => !showBranchSearch && setShowBranchSearch(true)}
                      className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${showBranchSearch ? 'w-64 px-2 cursor-default' : 'w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:scale-95'}`}
                    >
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowBranchSearch(!showBranchSearch); if (showBranchSearch) setSearchQuery(''); }}
                        className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                      >
                        <IconSearch size={16} />
                      </button>
                      {showBranchSearch && (
                        <input
                          autoFocus
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search branches..."
                          className="flex-1 ml-2 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-400"
                        />
                      )}
                    </div>
                  </div>

                  {/* Add Button */}
                  <button
                    onClick={() => handleOpenBranchModal()}
                    className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#FACC15] active:scale-95 transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5"
                    title="Add Branch"
                  >
                    <IconPlus size={16} stroke={2.5} className="text-[#212121]" />
                  </button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <ListView
                    columns={branchColumnsWithHandlers}
                    data={filteredBranches}
                  />
                </div>
              </div>
            </div>

            {/* Right Side Drawer Panel */}
            {showBranchModal && (
              <div className="col-span-12 xl:col-span-5">
                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                  {/* Drawer Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-[#F7F6F2] rounded-t-3xl">
                    <h2 className="text-base font-bold text-[#212121] tracking-tight">
                      {editingBranch ? 'Edit Branch' : 'Create New Branch'}
                    </h2>
                    <button
                      onClick={() => handleCloseBranchModal()}
                      className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-slate-200 transition text-slate-500"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </div>

                  {/* Drawer Body */}
                  <div className="p-5 space-y-4 overflow-y-auto max-h-[calc(100vh-280px)]">
                    {formError && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {formError}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Branch Code</label>
                        <input type="text" name="branchCode" value={branchFormData.branchCode || ''} onChange={handleBranchFormChange}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50" placeholder="e.g., BR-001" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Branch Name <span className="text-red-500">*</span></label>
                        <input type="text" name="branchName" value={branchFormData.branchName || ''} onChange={handleBranchFormChange}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50" placeholder="Branch name" required />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Branch Type</label>
                        <select name="branchType" value={branchFormData.branchType || 'BRANCH'} onChange={handleBranchFormChange}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50">
                          <option value="HEAD_OFFICE">Head Office</option>
                          <option value="BRANCH">Branch</option>
                          <option value="WAREHOUSE">Warehouse</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                        <select name="status" value={branchFormData.status || 'ACTIVE'} onChange={handleBranchFormChange}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50">
                          <option value="ACTIVE">Active</option>
                          <option value="INACTIVE">Inactive</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                        <input type="email" name="email" value={branchFormData.email || ''} onChange={handleBranchFormChange}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50" placeholder="branch@example.com" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
                        <input type="tel" name="phoneNumber" value={branchFormData.phoneNumber || ''} onChange={handleBranchFormChange}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50" placeholder="+966501234567" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Country <span className="text-red-500">*</span></label>
                        <SelectInput name="countryId" placeholder="Select Country"
                          selectOptions={countries.map((c) => ({ id: c.id, value: c.id, text: c.name }))}
                          value={branchFormData.countryId || ''}
                          setValue={async (val: string) => {
                            const matched = countries.find(c => String(c.id) === String(val) || c.name === val);
                            const selectedVal = matched ? matched.id : val;
                            setBranchFormData((prev) => ({ ...prev, countryId: selectedVal, cityId: '', districtId: '' }));
                            setCities([]); setDistricts([]);
                            if (selectedVal && isUUID(selectedVal)) {
                              try {
                                const citiesRes = await fetchHrCities(selectedVal);
                                if ((citiesRes as any).success && (citiesRes as any).data?.cities) setCities((citiesRes as any).data.cities);
                              } catch (err) { console.error('Failed to load cities:', err); }
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">{addressConfig.cityLabel} <span className="text-red-500">*</span></label>
                        <SelectInput name="cityId" placeholder={addressConfig.cityPlaceholder}
                          selectOptions={cities.map((c) => ({ id: c.id, value: c.id, text: c.name }))}
                          value={branchFormData.cityId || ''} allowCustom={true}
                          setValue={async (val: string) => {
                            const matched = cities.find(c => String(c.id) === String(val) || c.name === val);
                            const selectedVal = matched ? matched.id : val;
                            setBranchFormData((prev) => ({ ...prev, cityId: selectedVal, districtId: '' }));
                            setDistricts([]);
                            if (selectedVal && isUUID(selectedVal)) {
                              try {
                                const districtsRes = await fetchHrDistricts(selectedVal);
                                if ((districtsRes as any).success && (districtsRes as any).data?.districts) setDistricts((districtsRes as any).data.districts);
                              } catch (err) { console.error('Failed to load districts:', err); }
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">{addressConfig.districtLabel} <span className="text-red-500">*</span></label>
                        <SelectInput name="districtId" placeholder={addressConfig.districtPlaceholder}
                          selectOptions={districts.map((d) => ({ id: d.id, value: d.id, text: d.name }))}
                          value={branchFormData.districtId || ''} allowCustom={true}
                          setValue={(val: string) => {
                            const matched = districts.find(d => String(d.id) === String(val) || d.name === val);
                            const selectedVal = matched ? matched.id : val;
                            setBranchFormData((prev) => ({ ...prev, districtId: selectedVal }));
                          }}
                        />
                      </div>
                    </div>

                    {/* Map Toggle */}
                    <div className="space-y-3 pt-2 border-t border-gray-100">
                      <div className="flex justify-between items-center">
                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-2">
                          Pin Location on Map
                          {!!pinPosition && <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">📍 Pinned</span>}
                        </label>
                        <button type="button" onClick={handleToggleMap}
                          className="px-3 py-1.5 rounded-lg bg-[#FBD037] text-[#212121] text-xs font-semibold hover:bg-[#FACC15] transition-colors">
                          {showMap ? 'Hide Map' : 'Open Map'}
                        </button>
                      </div>

                      {showMap && (
                        <OutletGoogleMap onLocationPicked={handleMapPick} pinPosition={pinPosition} snappedPosition={snappedPosition} />
                      )}

                      <div className="flex flex-row gap-3">
                        {isSaudiArabia && (
                          <div className="w-[40%] relative">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Short Address <span className="text-red-500">*</span></label>
                            <div className="relative">
                              <input type="text" name="shortAddress" value={branchFormData.shortAddress || ''} onChange={handleShortAddressChange}
                                className={`w-full rounded-xl py-2 px-3 text-sm border outline-none pr-9 transition-all ${shortAddressLoading ? 'border-blue-300 bg-blue-50' : shortAddressError ? 'border-red-400 bg-red-50' : shortAddressWarning ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 bg-gray-50'} focus:border-[#FBD037]`}
                                placeholder="e.g. RHOO2375" autoComplete="off" disabled={shortAddressLoading} required={isSaudiArabia} />
                              <div className="absolute inset-y-0 right-2 flex items-center">
                                {shortAddressLoading && <Spinner />}
                                {!shortAddressLoading && shortAddressError && <span className="text-red-500 text-base">✕</span>}
                                {!shortAddressLoading && !shortAddressError && branchFormData.shortAddress?.length === 8 && branchFormData.fullAddress && <span className="text-green-500 text-base">✓</span>}
                              </div>
                            </div>
                            {shortAddressError && !shortAddressLoading && <p className="mt-1 text-xs text-red-500">⚠ {shortAddressError}</p>}
                          </div>
                        )}
                        {!isSaudiArabia && addressConfig.zipLabel && (
                          <div className="w-[40%]">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">{addressConfig.zipLabel}</label>
                            <input type="text" name="zipCode" value={branchFormData.zipCode || ''} onChange={handleBranchFormChange}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50" placeholder={addressConfig.zipPlaceholder} />
                          </div>
                        )}
                        <div className={isSaudiArabia || (!isSaudiArabia && addressConfig.zipLabel) ? 'flex-1' : 'w-full'}>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Full Address <span className="text-red-500">*</span></label>
                          <input type="text" name="fullAddress" value={branchFormData.fullAddress || ''} onChange={handleBranchFormChange}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50" placeholder="Enter full address details" required />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Latitude</label>
                          <input type="text" name="latitude" value={branchFormData.latitude || ''} onChange={handleBranchFormChange}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50" placeholder="e.g. 24.7136" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Longitude</label>
                          <input type="text" name="longitude" value={branchFormData.longitude || ''} onChange={handleBranchFormChange}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FBD037] bg-gray-50" placeholder="e.g. 46.6753" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Drawer Footer */}
                  <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-3">
                    <button onClick={() => handleCloseBranchModal()}
                      className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                      Cancel
                    </button>
                    <button onClick={handleBranchFormSubmit} disabled={formIsLoading}
                      className="px-5 py-2 text-sm font-bold text-[#212121] bg-[#FBD037] hover:bg-[#FACC15] rounded-xl transition-colors disabled:opacity-60 flex items-center gap-2">
                      {formIsLoading && <Spinner />}
                      {editingBranch ? 'Update Branch' : 'Create Branch'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>



        {/* Company Details Form Modal */}
        <CommonModal
          open={showCompanyModal}
          title="Edit Company Details"
          onClose={handleCloseCompanyModal}
          actionButtonText="Save Changes"
          actionButtonOnClick={handleCompanyFormSubmit}
          isPending={companyFormIsLoading}
        >
          <div className="p-6 bg-white space-y-4 min-w-[500px] max-w-2xl max-h-[calc(100vh-12rem)] overflow-y-auto">
            {companyFormError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {companyFormError}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input type="text" name="companyName" value={companyFormData.companyName || ""} onChange={handleCompanyFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FBD037]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" name="email" value={companyFormData.email || ""} onChange={handleCompanyFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FBD037]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input type="tel" name="phoneNumber" value={companyFormData.phoneNumber || ""} onChange={handleCompanyFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FBD037]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                <input type="text" name="industry" value={companyFormData.industry || ""} onChange={handleCompanyFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FBD037]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input type="url" name="website" value={companyFormData.website || ""} onChange={handleCompanyFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FBD037]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Founded Year</label>
                <input type="text" name="foundedYear" value={companyFormData.foundedYear || ""} onChange={handleCompanyFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FBD037]" />
              </div>
            </div>
          </div>
      </CommonModal>
    </div>
  );
}
